import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "./AuthContext";
import { formatApiErrorDetail } from "./api";
import { setPageMeta } from "../seo";

export default function AdminLogin() {
  const { login, user } = useAuth();

  useEffect(() => {
    setPageMeta({
      title: "Admin Sign In | Spire Inclusion",
      description: "Staff sign-in for the Spire Inclusion admin dashboard.",
      path: "/admin/login",
      noindex: true,
    });
  }, []);

  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Unable to log in.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-100 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-600 text-white">
            <Lock className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold text-stone-900">Staff sign in</h1>
            <p className="text-sm text-stone-500">Spire Inclusion admin</p>
          </div>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
          {error && (
            <div
              className="rounded-xl border-2 border-red-200 bg-red-50 p-3 text-base text-red-700"
              role="alert"
              data-testid="login-error"
            >
              {error}
            </div>
          )}
          <div>
            <label htmlFor="email" className="block text-lg font-medium text-stone-900 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 w-full rounded-xl border-2 border-stone-300 bg-white px-4 text-lg text-stone-900 focus-visible:border-orange-600"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-lg font-medium text-stone-900 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 w-full rounded-xl border-2 border-stone-300 bg-white px-4 text-lg text-stone-900 focus-visible:border-orange-600"
              data-testid="login-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex w-full items-center justify-center gap-2 px-6 py-3 rounded-xl bg-orange-600 text-white font-semibold text-lg hover:bg-orange-700 transition-colors disabled:opacity-70"
            data-testid="login-submit-button"
          >
            {submitting && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
